import * as THREE from 'three'
import Materials, { MaterialType } from './mesh/materials'
import Block from './mesh/block'
import Highlight from './highlight'
import Noise, { BiomeType } from './noise'
import SparseVoxelOctree from './svo'
import { getTerrainRenderCapacity } from './lod'
import FluidEngine from './fluid'

import Generate from './worker/generate?worker'

export enum BlockType {
  grass = 0,
  sand = 1,
  tree = 2,
  leaf = 3,
  dirt = 4,
  stone = 5,
  coal = 6,
  wood = 7,
  diamond = 8,
  quartz = 9,
  glass = 10,
  bedrock = 11,
  craftingTable = 12,
  water = 13,
  lava = 14,
  obsidian = 15,
  cobblestone = 16,
  tnt = 17,
  whitespaceGrass = 18,
  whitespaceDirt = 19,
  whitespaceLog = 20,
  whitespaceLeaf = 21,
  netherrack = 22,
  soulSand = 23,
  glowstone = 24,
  netherQuartz = 25,
  netherPortal = 26
}

export const isFluidType = (type: BlockType) =>
  type === BlockType.water || type === BlockType.lava

// natural lava fills cave air at or below this height
export const NATURAL_LAVA_LEVEL = 9

type TerrainWorkerResponse = MessageEvent<{
  requestId: number
  idMap: Map<string, number>
  arrays: Float32Array[]
  blocksCount: number[]
}>

export default class Terrain {
  constructor(scene: THREE.Scene, camera: THREE.PerspectiveCamera) {
    this.scene = scene
    this.camera = camera
    this.maxCount = this.getMaxCount()
    // main thread benefits hugely from cave memoization (collision checks the
    // natural column every frame); the worker keeps it off for memory
    this.noise.caveMemoEnabled = true
    this.highlight = new Highlight(scene, camera, this)
    this.fluid = new FluidEngine(this)
    this.cloud.count = 0
    // r137 InstancedMesh has no instance-aware bounds — the default frustum
    // test uses the unit-box bounding sphere at the world origin and culls the
    // whole mesh when the origin leaves the view
    this.cloud.frustumCulled = false
    this.scene.add(this.cloud)
    this.bindGenerateWorker()
  }

  bindGenerateWorker = () => {
    this.generateWorker.onmessage = (msg: TerrainWorkerResponse) => {
      if (msg.data.requestId !== this.terrainRequestId) return

      this.isTerrainGenerating = false
      this.idMap = msg.data.idMap
      this.blocksCount = msg.data.blocksCount

      for (let i = 0; i < msg.data.arrays.length; i++) {
        this.blocks[i].instanceMatrix = new THREE.InstancedBufferAttribute(
          msg.data.arrays[i],
          16
        )
      }

      for (const block of this.blocks) {
        block.instanceMatrix.needsUpdate = true
      }
      for (let i = 0; i < this.blocks.length; i++) {
        this.blocks[i].count = msg.data.blocksCount[i]
      }

      // the worker does not render custom fluid blocks — re-add their
      // instances now that the buffers have been replaced wholesale
      this.fluid.rebuildAfterRegen()

      // the player moved (or something requested another regen) while this pass
      // was running — kick off a fresh pass now for the current position
      if (this.pendingGenerate) {
        this.pendingGenerate = false
        this.generate()
      }
    }
  }

  restartGenerateWorker = () => {
    this.generateWorker.terminate()
    this.generateWorker = new Generate()
    this.bindGenerateWorker()
    this.isTerrainGenerating = false
  }

  // core properties
  scene: THREE.Scene
  camera: THREE.PerspectiveCamera
  distance = 3
  chunkSize = 24

  // terrain properties
  maxCount: number
  chunk = new THREE.Vector2(0, 0)
  previousChunk = new THREE.Vector2(0, 0)
  noise = new Noise()

  dimension: 'overworld' | 'nether' = 'overworld'
  overworldCustomBlocks: Block[] = []
  netherCustomBlocks: Block[] = []

  // materials
  materials = new Materials()
  materialType = [
    MaterialType.grass,
    MaterialType.sand,
    MaterialType.tree,
    MaterialType.leaf,
    MaterialType.dirt,
    MaterialType.stone,
    MaterialType.coal,
    MaterialType.wood,
    MaterialType.diamond,
    MaterialType.quartz,
    MaterialType.glass,
    MaterialType.bedrock,
    MaterialType.craftingTable,
    MaterialType.water,
    MaterialType.lava,
    MaterialType.obsidian,
    MaterialType.cobblestone,
    MaterialType.tnt,
    MaterialType.whitespaceGrass,
    MaterialType.whitespaceDirt,
    MaterialType.whitespaceLog,
    MaterialType.whitespaceLeaf,
    MaterialType.netherrack,
    MaterialType.soulSand,
    MaterialType.glowstone,
    MaterialType.netherQuartz,
    MaterialType.netherPortal
  ]

  // other properties
  blocks: THREE.InstancedMesh[] = []
  blocksCount: number[] = []
  blocksFactor = [
    1, 0.2, 0.1, 0.7, 0.6, 2.2, 0.2, 0.1, 0.1, 0.1, 0.1, 0.1, 0.05, 0.55,
    0.25, 0.08, 0.25, 0.04, 0.5, 0.4, 0.1, 0.5,
    2.5, 0.5, 0.3, 0.3, 0.1
  ]

  customBlocks: Block[] = []
  customBlockStore = new SparseVoxelOctree<Block>()
  customBlockIndex = new Map<string, Block>()
  customColumnIndex = new Map<string, Set<Block>>()
  customBlockStoreDirty = true
  customBlockStoreSource: Block[] | null = null
  customBlockStoreLength = 0
  highlight: Highlight
  fluid: FluidEngine

  idMap = new Map<string, number>()
  generateWorker = new Generate()
  terrainRequestId = 0
  isTerrainGenerating = false
  // set when a regen is requested while one is already in flight; the running
  // pass is allowed to finish (its result is still useful) and a fresh pass for
  // the current position fires the moment it returns. This replaces the old
  // "terminate the worker and start over" behaviour, which could leave terrain
  // perpetually unloaded if the player kept moving.
  pendingGenerate = false

  // cloud
  cloud = new THREE.InstancedMesh(
    new THREE.BoxGeometry(20, 5, 14),
    new THREE.MeshStandardMaterial({
      transparent: true,
      color: 0xffffff,
      opacity: 0.4
    }),
    256
  )
  cloudCount = 0
  cloudGap = 5

  getCount = (type: BlockType) => {
    return this.blocksCount[type]
  }

  getMaxCount = () => {
    return getTerrainRenderCapacity(this.distance, this.chunkSize)
  }

  setCount = (type: BlockType) => {
    this.blocksCount[type] = this.blocksCount[type] + 1
  }

  getElevation = (x: number, z: number, startY?: number) => {
    x = Math.trunc(x)
    z = Math.trunc(z)
    if (startY !== undefined) {
      for (let y = Math.floor(startY); y >= 0; y--) {
        if (this.isSolidForMobAt(x, y, z)) {
          return y
        }
      }
      return 0
    }
    if (this.dimension === 'nether') {
      for (let y = 55; y >= 0; y--) {
        if (this.isSolidForMobAt(x, y, z)) {
          return y
        }
      }
      return 0
    }
    let elevation = this.noise.getTerrainHeight(x, z)
    this.forEachCustomBlockInBounds(
      Math.floor(x),
      0,
      Math.floor(z),
      Math.floor(x),
      512,
      Math.floor(z),
      block => {
        if (block.placed && block.y >= elevation) {
          elevation = block.y + 1
        }
      }
    )
    return elevation
  }

  markCustomBlocksDirty = () => {
    this.customBlockStoreDirty = true
  }

  getBlockKey = (x: number, y: number, z: number) => {
    return `${Math.trunc(x)}_${Math.trunc(y)}_${Math.trunc(z)}`
  }

  getColumnKey = (x: number, z: number) => {
    return `${Math.trunc(x)}_${Math.trunc(z)}`
  }

  indexCustomBlock = (block: Block) => {
    this.customBlockStore.set(block.x, block.y, block.z, block)
    this.customBlockIndex.set(this.getBlockKey(block.x, block.y, block.z), block)

    const columnKey = this.getColumnKey(block.x, block.z)
    let column = this.customColumnIndex.get(columnKey)
    if (!column) {
      column = new Set()
      this.customColumnIndex.set(columnKey, column)
    }
    column.add(block)
  }

  syncCustomBlockStore = () => {
    if (
      !this.customBlockStoreDirty &&
      this.customBlockStoreSource === this.customBlocks &&
      this.customBlockStoreLength === this.customBlocks.length
    ) {
      return
    }

    this.customBlockStore.clear()
    this.customBlockIndex.clear()
    this.customColumnIndex.clear()
    for (const block of this.customBlocks) {
      this.indexCustomBlock(block)
    }
    this.customBlockStoreSource = this.customBlocks
    this.customBlockStoreLength = this.customBlocks.length
    this.customBlockStoreDirty = false
  }

  recordCustomBlock = (block: Block) => {
    this.syncCustomBlockStore()
    const existing = this.getCustomBlock(block.x, block.y, block.z)
    if (existing) {
      existing.type = block.type
      existing.placed = block.placed
      existing.flowLevel = block.flowLevel
      return existing
    }

    this.customBlocks.push(block)
    this.indexCustomBlock(block)
    this.customBlockStoreSource = this.customBlocks
    this.customBlockStoreLength = this.customBlocks.length
    this.customBlockStoreDirty = false
    return block
  }

  getCustomBlock = (x: number, y: number, z: number) => {
    this.syncCustomBlockStore()
    return this.customBlockIndex.get(this.getBlockKey(x, y, z)) ?? null
  }

  forEachCustomBlockInBounds = (
    minX: number,
    minY: number,
    minZ: number,
    maxX: number,
    maxY: number,
    maxZ: number,
    callback: (block: Block) => void
  ) => {
    this.syncCustomBlockStore()
    const startX = Math.floor(minX)
    const endX = Math.floor(maxX)
    const startZ = Math.floor(minZ)
    const endZ = Math.floor(maxZ)

    for (let x = startX; x <= endX; x++) {
      for (let z = startZ; z <= endZ; z++) {
        const column = this.customColumnIndex.get(this.getColumnKey(x, z))
        if (!column) continue
        for (const block of column) {
          if (block.y >= minY && block.y <= maxY) {
            callback(block)
          }
        }
      }
    }
  }

  getNaturalBlockType = (x: number, y: number, z: number): BlockType => {
    if (this.dimension === 'nether') {
      if (y <= 0 || y >= 63) return BlockType.bedrock
      const seed = this.noise.seed
      const h10 = this.noise.hash3(x, y, z, seed + 10.5)
      const h20 = this.noise.hash3(x, y, z, seed + 20.5)
      const h30 = this.noise.hash2(x, z, seed + 30.5)

      if (h10 > 0.96) return BlockType.netherQuartz
      if (y > 35 && !this.noise.isNetherCaveAt(x, y - 1, z) && h20 > 0.915) return BlockType.glowstone
      if (y >= 14 && y <= 21 && h30 > 0.8) return BlockType.soulSand
      return BlockType.netherrack
    }

    const elevation = this.noise.getTerrainHeight(x, z)
    const depth = elevation - y

    if (y <= 0) return BlockType.bedrock
    if (
      depth > 3 ||
      y < 23 ||
      this.noise.getStoneScore(x, z, elevation) > this.noise.stoneThreshold
    ) {
      return this.noise.getCoalScore(x, y, z) > this.noise.coalThreshold
        ? BlockType.coal
        : BlockType.stone
    }
    const biome = this.noise.getBiomeAt(x, z, elevation)
    if (biome === BiomeType.omori) {
      return depth === 0 ? BlockType.whitespaceGrass : BlockType.whitespaceDirt
    }
    if (biome === BiomeType.snow) {
      return depth === 0 ? BlockType.quartz : BlockType.dirt
    }
    if (biome === BiomeType.beach || biome === BiomeType.dunes) {
      return BlockType.sand
    }
    if (biome === BiomeType.scrub) {
      return BlockType.dirt
    }
    if (biome === BiomeType.rockyRidges) {
      return BlockType.stone
    }
    return depth <= 1 ? BlockType.dirt : BlockType.dirt
  }

  isWithinActiveTerrain = (x: number, z: number) => {
    const chunkX = Math.floor(x / this.chunkSize)
    const chunkZ = Math.floor(z / this.chunkSize)
    return (
      Math.max(Math.abs(chunkX - this.chunk.x), Math.abs(chunkZ - this.chunk.y)) <=
      this.distance
    )
  }

  isNaturalSolidAt = (x: number, y: number, z: number) => {
    if (this.dimension === 'nether') {
      if (y <= 0 || y >= 63) return true
      return !this.noise.isNetherCaveAt(x, y, z)
    }
    if (y < 0) return false
    const elevation = this.noise.getTerrainHeight(x, z)
    if (y > elevation) return false
    if (y <= 0) return true
    return !this.noise.isCaveAt(x, y, z, elevation)
  }

  isNaturalWaterAt = (x: number, y: number, z: number) => {
    if (this.dimension === 'nether') return false
    const elevation = this.noise.getTerrainHeight(x, z)
    return y > elevation && y <= this.noise.seaLevel
  }

  isNaturalLavaAt = (x: number, y: number, z: number) => {
    if (this.dimension === 'nether') {
      if (y <= 0 || y > 16) return false
      return this.noise.isNetherCaveAt(x, y, z)
    }
    if (y <= 0 || y > NATURAL_LAVA_LEVEL) return false
    const elevation = this.noise.getTerrainHeight(x, z)
    if (y > elevation) return false
    return this.noise.isCaveAt(x, y, z, elevation)
  }

  isPortalAt = (x: number, y: number, z: number) => {
    const custom = this.getCustomBlock(x, y, z)
    return custom !== null && custom.placed && custom.type === BlockType.netherPortal
  }

  isObsidianBlock = (x: number, y: number, z: number) => {
    const custom = this.getCustomBlock(x, y, z)
    if (custom) {
      return custom.placed && custom.type === BlockType.obsidian
    }
    return this.getNaturalBlockType(x, y, z) === BlockType.obsidian
  }

  isValidPortalFrameX = (bx: number, by: number, bz: number) => {
    if (!this.isObsidianBlock(bx + 1, by, bz) || !this.isObsidianBlock(bx + 2, by, bz)) return false
    if (!this.isObsidianBlock(bx + 1, by + 4, bz) || !this.isObsidianBlock(bx + 2, by + 4, bz)) return false
    if (!this.isObsidianBlock(bx, by + 1, bz) || !this.isObsidianBlock(bx, by + 2, bz) || !this.isObsidianBlock(bx, by + 3, bz)) return false
    if (!this.isObsidianBlock(bx + 3, by + 1, bz) || !this.isObsidianBlock(bx + 3, by + 2, bz) || !this.isObsidianBlock(bx + 3, by + 3, bz)) return false
    return true
  }

  isValidPortalFrameZ = (bx: number, by: number, bz: number) => {
    if (!this.isObsidianBlock(bx, by, bz + 1) || !this.isObsidianBlock(bx, by, bz + 2)) return false
    if (!this.isObsidianBlock(bx, by + 4, bz + 1) || !this.isObsidianBlock(bx, by + 4, bz + 2)) return false
    if (!this.isObsidianBlock(bx, by + 1, bz) || !this.isObsidianBlock(bx, by + 2, bz) || !this.isObsidianBlock(bx, by + 3, bz)) return false
    if (!this.isObsidianBlock(bx, by + 1, bz + 3) || !this.isObsidianBlock(bx, by + 2, bz + 3) || !this.isObsidianBlock(bx, by + 3, bz + 3)) return false
    return true
  }

  checkAndActivatePortal = (x: number, y: number, z: number) => {
    x = Math.round(x)
    y = Math.round(y)
    z = Math.round(z)
    for (let bx = x - 3; bx <= x; bx++) {
      for (let by = y - 4; by <= y; by++) {
        if (this.isValidPortalFrameX(bx, by, z)) {
          this.fillPortalX(bx, by, z)
          return
        }
      }
    }
    for (let bz = z - 3; bz <= z; bz++) {
      for (let by = y - 4; by <= y; by++) {
        if (this.isValidPortalFrameZ(x, by, bz)) {
          this.fillPortalZ(x, by, bz)
          return
        }
      }
    }
  }

  fillPortalX = (bx: number, by: number, bz: number) => {
    for (let dx = 1; dx <= 2; dx++) {
      for (let dy = 1; dy <= 3; dy++) {
        this.placePortalBlock(bx + dx, by + dy, bz)
      }
    }
  }

  fillPortalZ = (bx: number, by: number, bz: number) => {
    for (let dz = 1; dz <= 2; dz++) {
      for (let dy = 1; dy <= 3; dy++) {
        this.placePortalBlock(bx, by + dy, bz + dz)
      }
    }
  }

  placePortalBlock = (x: number, y: number, z: number) => {
    const block = new Block(x, y, z, BlockType.netherPortal, true)
    this.recordCustomBlock(block)
    const matrix = new THREE.Matrix4().setPosition(x, y, z)
    this.blocks[BlockType.netherPortal].setMatrixAt(
      this.getCount(BlockType.netherPortal),
      matrix
    )
    this.setCount(BlockType.netherPortal)
    this.blocks[BlockType.netherPortal].count = this.getCount(BlockType.netherPortal)
    this.blocks[BlockType.netherPortal].instanceMatrix.needsUpdate = true
  }

  checkAndDeactivatePortals = (x: number, y: number, z: number) => {
    x = Math.round(x)
    y = Math.round(y)
    z = Math.round(z)
    const radius = 3
    const portalsToRemove: { x: number; y: number; z: number }[] = []

    for (let dx = -radius; dx <= radius; dx++) {
      for (let dy = -radius; dy <= radius; dy++) {
        for (let dz = -radius; dz <= radius; dz++) {
          const px = x + dx
          const py = y + dy
          const pz = z + dz

          const custom = this.getCustomBlock(px, py, pz)
          if (custom && custom.placed && custom.type === BlockType.netherPortal) {
            let hasValidFrame = false
            for (let bx = px - 2; bx <= px - 1; bx++) {
              for (let by = py - 3; by <= py - 1; by++) {
                if (this.isValidPortalFrameX(bx, by, pz)) {
                  hasValidFrame = true
                  break
                }
              }
              if (hasValidFrame) break
            }

            if (!hasValidFrame) {
              for (let bz = pz - 2; bz <= pz - 1; bz++) {
                for (let by = py - 3; by <= py - 1; by++) {
                  if (this.isValidPortalFrameZ(px, by, bz)) {
                    hasValidFrame = true
                    break
                  }
                }
                if (hasValidFrame) break
              }
            }

            if (!hasValidFrame) {
              portalsToRemove.push({ x: px, y: py, z: pz })
            }
          }
        }
      }
    }

    for (const p of portalsToRemove) {
      const custom = this.getCustomBlock(p.x, p.y, p.z)
      if (custom) {
        custom.placed = false
      }
    }
    if (portalsToRemove.length > 0) {
      this.generate()
    }
  }

  createDestinationPortal = (x: number, y: number, z: number) => {
    for (let dx = -1; dx <= 1; dx++) {
      for (let dz = -1; dz <= 1; dz++) {
        this.recordCustomBlock(new Block(x + dx, y - 1, z + dz, BlockType.obsidian, true))
      }
    }

    const obs = [
      { x: x - 1, y: y, z },
      { x: x - 1, y: y + 1, z },
      { x: x - 1, y: y + 2, z },
      { x: x - 1, y: y + 3, z },
      { x: x + 2, y: y, z },
      { x: x + 2, y: y + 1, z },
      { x: x + 2, y: y + 2, z },
      { x: x + 2, y: y + 3, z },
      { x: x, y: y - 1, z },
      { x: x + 1, y: y - 1, z },
      { x: x, y: y + 3, z },
      { x: x + 1, y: y + 3, z }
    ]

    for (const ob of obs) {
      this.recordCustomBlock(new Block(ob.x, ob.y, ob.z, BlockType.obsidian, true))
    }

    for (let dx = 0; dx <= 1; dx++) {
      for (let dy = 0; dy <= 2; dy++) {
        this.recordCustomBlock(new Block(x + dx, y + dy, z, BlockType.netherPortal, true))
      }
    }
  }

  isNaturalFluidAt = (type: BlockType, x: number, y: number, z: number) => {
    return type === BlockType.water
      ? this.isNaturalWaterAt(x, y, z)
      : this.isNaturalLavaAt(x, y, z)
  }

  isFluidAt = (type: BlockType, x: number, y: number, z: number) => {
    const custom = this.getCustomBlock(x, y, z)
    if (custom) {
      return custom.placed && custom.type === type
    }
    return this.isNaturalFluidAt(type, x, y, z)
  }

  isWaterAt = (x: number, y: number, z: number) =>
    this.isFluidAt(BlockType.water, x, y, z)

  isLavaAt = (x: number, y: number, z: number) =>
    this.isFluidAt(BlockType.lava, x, y, z)

  getWaterSurfaceYAt = (x: number, z: number) => {
    x = Math.trunc(x)
    z = Math.trunc(z)
    let surfaceY: number | null = null
    const elevation = this.noise.getTerrainHeight(x, z)

    if (this.noise.seaLevel > elevation) {
      surfaceY = this.noise.seaLevel
    }

    this.forEachCustomBlockInBounds(x, 0, z, x, this.noise.terrainMax + 16, z, block => {
      if (!block.placed || block.type !== BlockType.water) return
      surfaceY = surfaceY === null ? block.y : Math.max(surfaceY, block.y)
    })

    return surfaceY
  }

  // cell-solidity test for mob physics, projectiles, and line-of-sight —
  // fluids and cave/mined-out air are not solid
  isSolidForMobAt = (x: number, y: number, z: number) => {
    x = Math.round(x)
    y = Math.round(y)
    z = Math.round(z)
    const custom = this.getCustomBlock(x, y, z)
    if (custom) return custom.placed && !isFluidType(custom.type) && custom.type !== BlockType.netherPortal
    return this.isNaturalSolidAt(x, y, z)
  }

  // true when a fluid block could occupy the cell (air, or a tombstoned
  // mined-out cell); occupied cells — including other fluids — return false,
  // fluid-vs-fluid contact is resolved by FluidEngine interactions instead
  canFluidOccupy = (x: number, y: number, z: number) => {
    if (y <= 0 || y > this.noise.terrainMax + 8) return false
    if (!this.isWithinActiveTerrain(x, z)) return false

    const custom = this.getCustomBlock(x, y, z)
    if (custom) return !custom.placed
    if (this.isNaturalWaterAt(x, y, z)) return false
    if (this.isNaturalLavaAt(x, y, z)) return false
    if (this.isNaturalSolidAt(x, y, z)) return false

    return true
  }

  // remove a custom block record entirely (used when fluids drain — unlike
  // mined solids they must not leave placed=false tombstones behind)
  deleteCustomBlock = (block: Block) => {
    this.syncCustomBlockStore()
    const index = this.customBlocks.indexOf(block)
    if (index !== -1) {
      const last = this.customBlocks.length - 1
      this.customBlocks[index] = this.customBlocks[last]
      this.customBlocks.pop()
    }
    this.customBlockIndex.delete(this.getBlockKey(block.x, block.y, block.z))
    this.customBlockStore.delete(block.x, block.y, block.z)
    const column = this.customColumnIndex.get(this.getColumnKey(block.x, block.z))
    column?.delete(block)
    this.customBlockStoreSource = this.customBlocks
    this.customBlockStoreLength = this.customBlocks.length
  }

  // add a solid block from simulation code (fluid interactions, explosions)
  // — the instanced-mesh equivalent of a player right-click placement
  placeSolidFromSim = (x: number, y: number, z: number, type: BlockType) => {
    if (this.getCount(type) >= this.blocks[type].instanceMatrix.count) {
      return false
    }
    const matrix = new THREE.Matrix4()
    matrix.setPosition(x, y, z)
    this.blocks[type].setMatrixAt(this.getCount(type), matrix)
    this.setCount(type)
    this.blocks[type].count = this.getCount(type)
    this.blocks[type].instanceMatrix.needsUpdate = true
    this.recordCustomBlock(new Block(x, y, z, type, true))
    return true
  }

  initBlocks = () => {
    // reset
    for (const block of this.blocks) {
      this.scene.remove(block)
    }
    this.blocks = []

    // create instance meshes
    const geometry = new THREE.BoxGeometry()

    for (let i = 0; i < this.materialType.length; i++) {
      const blockCapacity = Math.ceil(this.maxCount * this.blocksFactor[i])
      let block = new THREE.InstancedMesh(
        geometry,
        this.materials.get(this.materialType[i]),
        blockCapacity
      )
      block.name = BlockType[i]
      block.castShadow = false
      block.receiveShadow = false
      block.count = 0
      // r137 frustum-culls instanced meshes by the unit-box bounding sphere
      // at the origin — terrain would vanish when looking away from (0,0,0)
      block.frustumCulled = false
      if (i === BlockType.water) {
        block.renderOrder = 10
      }
      this.blocks.push(block)
      this.scene.add(block)
    }

    this.blocksCount = new Array(this.materialType.length).fill(0)
    this.fluid.onBlocksReset()
  }

  resetBlocks = () => {
    // reest count and instance matrix
    for (let i = 0; i < this.blocks.length; i++) {
      this.blocks[i].instanceMatrix = new THREE.InstancedBufferAttribute(
        new Float32Array(Math.ceil(this.maxCount * this.blocksFactor[i]) * 16),
        16
      )
      this.blocks[i].count = 0
    }
  }

  generate = (options?: { force?: boolean }) => {
    this.syncCustomBlockStore()

    // A regen is already running. Don't throw its work away — that's what made
    // terrain fail to load while moving (especially in the slow-to-generate
    // Nether). Instead remember that another pass is needed and start it as
    // soon as this one returns. `force` is used only when the in-flight result
    // would be wrong for the new state (e.g. a dimension switch), in which case
    // we do discard it.
    if (this.isTerrainGenerating) {
      if (!options?.force) {
        this.pendingGenerate = true
        return
      }
      this.restartGenerateWorker()
    }
    this.pendingGenerate = false

    this.blocksCount = new Array(this.blocks.length).fill(0)
    const requestId = ++this.terrainRequestId
    this.isTerrainGenerating = true

    // post work to generate worker
    this.generateWorker.postMessage({
      requestId,
      distance: this.distance,
      chunk: { x: this.chunk.x, y: this.chunk.y },
      noiseSeed: this.noise.seed,
      treeSeed: this.noise.treeSeed,
      stoneSeed: this.noise.stoneSeed,
      coalSeed: this.noise.coalSeed,
      leafSeed: this.noise.leafSeed,
      seaLevel: this.noise.seaLevel,
      blocksFactor: this.blocksFactor,
      blocksCount: this.blocksCount,
      customBlocks: this.customBlocks,
      chunkSize: this.chunkSize,
      dimension: this.dimension
    })

    // cloud

    if (this.cloudGap++ > 5) {
      this.cloudGap = 0
      this.cloud.instanceMatrix = new THREE.InstancedBufferAttribute(
        new Float32Array(this.cloud.instanceMatrix.count * 16),
        16
      )
      this.cloudCount = 0
      for (
        let x =
          -this.chunkSize * this.distance * 3 + this.chunkSize * this.chunk.x;
        x <
        this.chunkSize * this.distance * 3 +
          this.chunkSize +
          this.chunkSize * this.chunk.x;
        x += 20
      ) {
        for (
          let z =
            -this.chunkSize * this.distance * 3 + this.chunkSize * this.chunk.y;
          z <
          this.chunkSize * this.distance * 3 +
            this.chunkSize +
            this.chunkSize * this.chunk.y;
          z += 20
        ) {
          const matrix = new THREE.Matrix4()
          matrix.setPosition(x, 80 + (Math.random() - 0.5) * 30, z)

          if (
            Math.random() > 0.88 &&
            this.cloudCount < this.cloud.instanceMatrix.count
          ) {
            this.cloud.setMatrixAt(this.cloudCount++, matrix)
          }
        }
      }
      this.cloud.count = this.cloudCount
      this.cloud.instanceMatrix.needsUpdate = true
    }
  }

  // reveal the freshly-exposed neighbours after a block is removed, without
  // waiting for a full chunk regen. Works in both dimensions — the previous
  // version only understood overworld terrain, so Nether mining never revealed
  // its tunnel walls until the (slow) regen caught up.
  generateAdjacentBlocks = (position: THREE.Vector3) => {
    const { x, y, z } = position

    this.buildBlock(new THREE.Vector3(x, y - 1, z))
    this.buildBlock(new THREE.Vector3(x, y + 1, z))
    this.buildBlock(new THREE.Vector3(x - 1, y, z))
    this.buildBlock(new THREE.Vector3(x + 1, y, z))
    this.buildBlock(new THREE.Vector3(x, y, z - 1))
    this.buildBlock(new THREE.Vector3(x, y, z + 1))

    this.fluid.notifyBlockChanged(position.x, position.y, position.z)
  }

  buildBlock = (position: THREE.Vector3) => {
    this.syncCustomBlockStore()

    // only fill cells that actually hold a natural solid block in the current
    // dimension — air, caves and above-surface cells stay empty
    if (!this.isNaturalSolidAt(position.x, position.y, position.z)) {
      return
    }

    // already rendered by the terrain worker — don't add a duplicate instance
    if (
      this.idMap.has(this.getBlockKey(position.x, position.y, position.z))
    ) {
      return
    }

    // a custom block already owns this cell (placed block, or a mined-out
    // tombstone we must not refill)
    if (this.customBlockStore.get(position.x, position.y, position.z)) {
      return
    }

    const type = this.getNaturalBlockType(position.x, position.y, position.z)

    // build block
    if (this.getCount(type) >= this.blocks[type].instanceMatrix.count) {
      return
    }

    this.recordCustomBlock(
      new Block(position.x, position.y, position.z, type, true)
    )

    const matrix = new THREE.Matrix4()
    matrix.setPosition(position)
    this.blocks[type].setMatrixAt(this.getCount(type), matrix)
    this.blocks[type].instanceMatrix.needsUpdate = true
    this.setCount(type)
    this.blocks[type].count = this.getCount(type)
  }

  generateBurgerKingStructure = () => {
    const noise = this.noise
    const foundationY = noise.getFoundationHeight()

    // 1. Floor (checkerboard pattern)
    // Floor is at y = foundationY - 1. We fill the interior x from 21 to 38, z from -8 to 8
    for (let x = 21; x <= 38; x++) {
      for (let z = -8; z <= 8; z++) {
        const type = (x + z) % 2 === 0 ? BlockType.obsidian : BlockType.quartz
        this.customBlocks.push(new Block(x, foundationY - 1, z, type, true))
      }
    }

    // 2. Foundation padding under walls
    for (let x = 20; x <= 39; x++) {
      this.customBlocks.push(new Block(x, foundationY - 1, -9, BlockType.quartz, true))
      this.customBlocks.push(new Block(x, foundationY - 1, 9, BlockType.quartz, true))
    }
    for (let z = -8; z <= 8; z++) {
      this.customBlocks.push(new Block(20, foundationY - 1, z, BlockType.quartz, true))
      this.customBlocks.push(new Block(39, foundationY - 1, z, BlockType.quartz, true))
    }

    // 3. Walls (y from foundationY to foundationY + 4)
    // Front Wall (x = 20, z from -9 to 9)
    for (let y = foundationY; y <= foundationY + 4; y++) {
      for (let z = -9; z <= 9; z++) {
        // Pillars of Quartz at z = -9, -5, 0, 5, 9
        if (z === -9 || z === -5 || z === 0 || z === 5 || z === 9) {
          this.customBlocks.push(new Block(20, y, z, BlockType.quartz, true))
        } else if (y === foundationY + 4) {
          // Top row: Wood in the middle, Quartz on the outer segments
          const type = (z >= -4 && z <= 4) ? BlockType.wood : BlockType.quartz
          this.customBlocks.push(new Block(20, y, z, type, true))
        } else {
          // Open doorway at z = -2 to -1, y from foundationY to foundationY + 2
          if (!(z >= -2 && z <= -1 && y <= foundationY + 2)) {
            // Glass window
            this.customBlocks.push(new Block(20, y, z, BlockType.glass, true))
          }
        }
      }
    }

    // Left Wall (z = -9, x from 21 to 38)
    for (let y = foundationY; y <= foundationY + 4; y++) {
      for (let x = 21; x <= 38; x++) {
        if (x === 25 || x === 30 || x === 35) {
          this.customBlocks.push(new Block(x, y, -9, BlockType.quartz, true))
        } else if (y === foundationY + 4) {
          this.customBlocks.push(new Block(x, y, -9, BlockType.quartz, true))
        } else {
          this.customBlocks.push(new Block(x, y, -9, BlockType.glass, true))
        }
      }
    }

    // Right Wall (z = 9, x from 21 to 38)
    for (let y = foundationY; y <= foundationY + 4; y++) {
      for (let x = 21; x <= 38; x++) {
        if (x === 25 || x === 30 || x === 35) {
          this.customBlocks.push(new Block(x, y, 9, BlockType.quartz, true))
        } else if (y === foundationY + 4) {
          this.customBlocks.push(new Block(x, y, 9, BlockType.quartz, true))
        } else {
          this.customBlocks.push(new Block(x, y, 9, BlockType.glass, true))
        }
      }
    }

    // Back Wall (x = 39, z from -9 to 9) - Solid Quartz (kitchen area)
    for (let y = foundationY; y <= foundationY + 4; y++) {
      for (let z = -9; z <= 9; z++) {
        this.customBlocks.push(new Block(39, y, z, BlockType.quartz, true))
      }
    }

    // 4. Roof (y = foundationY + 5)
    // Cover x from 20 to 39, z from -9 to 9
    for (let x = 20; x <= 39; x++) {
      for (let z = -9; z <= 9; z++) {
        if (x === 20 || x === 39 || z === -9 || z === 9) {
          this.customBlocks.push(new Block(x, foundationY + 5, z, BlockType.obsidian, true))
        } else {
          this.customBlocks.push(new Block(x, foundationY + 5, z, BlockType.quartz, true))
        }
      }
    }

    // Modern BK logo accent on front facade (x = 19, y = foundationY + 5)
    for (let z = -4; z <= 4; z++) {
      const type = (z === -4 || z === -3 || z === 3 || z === 4) ? BlockType.tnt : BlockType.sand
      this.customBlocks.push(new Block(19, foundationY + 5, z, type, true))
    }

    // 5. Interior Layout
    // Ordering Counter (x = 30, z from -6 to 6, except z = 5 for employee walk-through)
    for (let z = -6; z <= 6; z++) {
      if (z === 5) continue
      this.customBlocks.push(new Block(30, foundationY, z, BlockType.craftingTable, true))
    }

    // Kitchen Grill & Prep
    this.customBlocks.push(new Block(35, foundationY, -3, BlockType.cobblestone, true))
    this.customBlocks.push(new Block(35, foundationY + 1, -3, BlockType.coal, true)) // Charcoal grill
    this.customBlocks.push(new Block(36, foundationY, -3, BlockType.craftingTable, true)) // Prep table

    // Dining Area Tables & Seats
    // Table 1
    this.customBlocks.push(new Block(24, foundationY, -4, BlockType.wood, true))
    this.customBlocks.push(new Block(24, foundationY, -5, BlockType.quartz, true))
    this.customBlocks.push(new Block(24, foundationY, -3, BlockType.quartz, true))
    // Table 2
    this.customBlocks.push(new Block(24, foundationY, 4, BlockType.wood, true))
    this.customBlocks.push(new Block(24, foundationY, 3, BlockType.quartz, true))
    this.customBlocks.push(new Block(24, foundationY, 5, BlockType.quartz, true))
    // Table 3
    this.customBlocks.push(new Block(27, foundationY, -4, BlockType.wood, true))
    this.customBlocks.push(new Block(27, foundationY, -5, BlockType.quartz, true))
    this.customBlocks.push(new Block(27, foundationY, -3, BlockType.quartz, true))
    // Table 4
    this.customBlocks.push(new Block(27, foundationY, 4, BlockType.wood, true))
    this.customBlocks.push(new Block(27, foundationY, 3, BlockType.quartz, true))
    this.customBlocks.push(new Block(27, foundationY, 5, BlockType.quartz, true))

    // 6. Outside Signpost
    // Pole at (22, -12) from foundationY to foundationY + 8
    for (let y = foundationY; y < foundationY + 9; y++) {
      this.customBlocks.push(new Block(22, y, -12, BlockType.obsidian, true))
    }
    // Signboard 5x5 vertical pattern
    const signPattern = [
      [BlockType.glass, BlockType.sand, BlockType.sand, BlockType.sand, BlockType.glass],
      [BlockType.diamond, BlockType.quartz, BlockType.tnt, BlockType.quartz, BlockType.diamond],
      [BlockType.diamond, BlockType.tnt, BlockType.tnt, BlockType.tnt, BlockType.diamond],
      [BlockType.diamond, BlockType.quartz, BlockType.tnt, BlockType.quartz, BlockType.diamond],
      [BlockType.glass, BlockType.sand, BlockType.sand, BlockType.sand, BlockType.glass]
    ]
    for (let row = 0; row < 5; row++) {
      const yVal = foundationY + 13 - row
      const rowBlocks = signPattern[row]
      for (let col = 0; col < 5; col++) {
        const zVal = -14 + col
        const type = rowBlocks[col]
        if (type !== BlockType.glass) {
          this.customBlocks.push(new Block(22, yVal, zVal, type, true))
        }
      }
    }
    // 7. Nether Portal inside Burger King (at x = 22, z = 5 to 8, y = foundationY - 1 to foundationY + 3)
    const portalX = 22
    // Bottom row
    for (let z = 5; z <= 8; z++) {
      this.customBlocks.push(new Block(portalX, foundationY - 1, z, BlockType.obsidian, true))
    }
    // Left & Right columns
    for (let y = foundationY; y <= foundationY + 2; y++) {
      this.customBlocks.push(new Block(portalX, y, 5, BlockType.obsidian, true))
      this.customBlocks.push(new Block(portalX, y, 8, BlockType.obsidian, true))
    }
    // Top row
    for (let z = 5; z <= 8; z++) {
      this.customBlocks.push(new Block(portalX, foundationY + 3, z, BlockType.obsidian, true))
    }
    // Portal blocks (the gateway)
    for (let y = foundationY; y <= foundationY + 2; y++) {
      for (let z = 6; z <= 7; z++) {
        this.customBlocks.push(new Block(portalX, y, z, BlockType.netherPortal, true))
      }
    }

    this.markCustomBlocksDirty()
  }

  generateMcDonaldsStructure = () => {
    const noise = this.noise
    const foundationY = noise.getFoundationHeight()

    // 1. Floor (oak wood planks)
    // Floor is at y = foundationY - 1. We fill the interior x from 21 to 38, z from 16 to 32
    for (let x = 21; x <= 38; x++) {
      for (let z = 16; z <= 32; z++) {
        this.customBlocks.push(new Block(x, foundationY - 1, z, BlockType.wood, true))
      }
    }

    // 2. Foundation padding under walls
    for (let x = 20; x <= 39; x++) {
      this.customBlocks.push(new Block(x, foundationY - 1, 15, BlockType.quartz, true))
      this.customBlocks.push(new Block(x, foundationY - 1, 33, BlockType.quartz, true))
    }
    for (let z = 16; z <= 32; z++) {
      this.customBlocks.push(new Block(20, foundationY - 1, z, BlockType.quartz, true))
      this.customBlocks.push(new Block(39, foundationY - 1, z, BlockType.quartz, true))
    }

    // 3. Walls (y from foundationY to foundationY + 4)
    // Front Wall (x = 20, z from 15 to 33)
    for (let y = foundationY; y <= foundationY + 4; y++) {
      for (let z = 15; z <= 33; z++) {
        // Pillars of Quartz at z = 15, 19, 24, 29, 33
        if (z === 15 || z === 19 || z === 24 || z === 29 || z === 33) {
          this.customBlocks.push(new Block(20, y, z, BlockType.quartz, true))
        } else if (y === foundationY + 4) {
          // Top row: Red (TNT)
          this.customBlocks.push(new Block(20, y, z, BlockType.tnt, true))
        } else {
          // Open doorway at z = 21 to 22, y from foundationY to foundationY + 2
          if (!(z >= 21 && z <= 22 && y <= foundationY + 2)) {
            // Glass window
            this.customBlocks.push(new Block(20, y, z, BlockType.glass, true))
          }
        }
      }
    }

    // Left Wall (z = 15, x from 21 to 38)
    for (let y = foundationY; y <= foundationY + 4; y++) {
      for (let x = 21; x <= 38; x++) {
        if (x === 25 || x === 30 || x === 35) {
          this.customBlocks.push(new Block(x, y, 15, BlockType.quartz, true))
        } else if (y === foundationY + 4) {
          this.customBlocks.push(new Block(x, y, 15, BlockType.tnt, true))
        } else {
          this.customBlocks.push(new Block(x, y, 15, BlockType.obsidian, true)) // Obsidian walls
        }
      }
    }

    // Right Wall (z = 33, x from 21 to 38)
    for (let y = foundationY; y <= foundationY + 4; y++) {
      for (let x = 21; x <= 38; x++) {
        if (x === 25 || x === 30 || x === 35) {
          this.customBlocks.push(new Block(x, y, 33, BlockType.quartz, true))
        } else if (y === foundationY + 4) {
          this.customBlocks.push(new Block(x, y, 33, BlockType.tnt, true))
        } else {
          this.customBlocks.push(new Block(x, y, 33, BlockType.obsidian, true)) // Obsidian walls
        }
      }
    }

    // Back Wall (x = 39, z from 15 to 33) - Solid Obsidian (kitchen area)
    for (let y = foundationY; y <= foundationY + 4; y++) {
      for (let z = 15; z <= 33; z++) {
        this.customBlocks.push(new Block(39, y, z, BlockType.obsidian, true))
      }
    }

    // 4. Roof (y = foundationY + 5)
    // Cover x from 20 to 39, z from 15 to 33
    for (let x = 20; x <= 39; x++) {
      for (let z = 15; z <= 33; z++) {
        if (x === 20 || x === 39 || z === 15 || z === 33) {
          this.customBlocks.push(new Block(x, foundationY + 5, z, BlockType.tnt, true)) // Red border trim
        } else {
          this.customBlocks.push(new Block(x, foundationY + 5, z, BlockType.quartz, true))
        }
      }
    }

    // Facade Logo: Yellow "M" above the doorway (x = 19, y = foundationY + 5 to 7, z = 20 to 24)
    const facadeLogoPattern = [
      [BlockType.sand, BlockType.glass, BlockType.glass, BlockType.glass, BlockType.sand],
      [BlockType.sand, BlockType.sand, BlockType.glass, BlockType.sand, BlockType.sand],
      [BlockType.sand, BlockType.glass, BlockType.sand, BlockType.glass, BlockType.sand]
    ]
    for (let row = 0; row < 3; row++) {
      const yVal = foundationY + 7 - row
      const rowBlocks = facadeLogoPattern[row]
      for (let col = 0; col < 5; col++) {
        const zVal = 20 + col
        const type = rowBlocks[col]
        if (type !== BlockType.glass) {
          this.customBlocks.push(new Block(19, yVal, zVal, type, true))
        }
      }
    }

    // 5. Interior Layout
    // Counter (x = 30, z from 18 to 30, except z = 29 for employees walk-through)
    for (let z = 18; z <= 30; z++) {
      if (z === 29) continue
      this.customBlocks.push(new Block(30, foundationY, z, BlockType.craftingTable, true))
    }

    // Kitchen Grill & Fryer
    this.customBlocks.push(new Block(35, foundationY, 21, BlockType.cobblestone, true))
    this.customBlocks.push(new Block(35, foundationY + 1, 21, BlockType.coal, true)) // Grill
    this.customBlocks.push(new Block(35, foundationY, 23, BlockType.cobblestone, true))
    this.customBlocks.push(new Block(35, foundationY + 1, 23, BlockType.coal, true)) // Fryer
    this.customBlocks.push(new Block(36, foundationY, 22, BlockType.craftingTable, true)) // Prep table

    // Dining Area Tables & Seats
    // Table 1
    this.customBlocks.push(new Block(24, foundationY, 18, BlockType.obsidian, true))
    this.customBlocks.push(new Block(24, foundationY, 17, BlockType.sand, true))
    this.customBlocks.push(new Block(24, foundationY, 19, BlockType.sand, true))
    // Table 2
    this.customBlocks.push(new Block(24, foundationY, 28, BlockType.obsidian, true))
    this.customBlocks.push(new Block(24, foundationY, 27, BlockType.sand, true))
    this.customBlocks.push(new Block(24, foundationY, 29, BlockType.sand, true))
    // Table 3
    this.customBlocks.push(new Block(27, foundationY, 18, BlockType.obsidian, true))
    this.customBlocks.push(new Block(27, foundationY, 17, BlockType.sand, true))
    this.customBlocks.push(new Block(27, foundationY, 19, BlockType.sand, true))
    // Table 4
    this.customBlocks.push(new Block(27, foundationY, 28, BlockType.obsidian, true))
    this.customBlocks.push(new Block(27, foundationY, 27, BlockType.sand, true))
    this.customBlocks.push(new Block(27, foundationY, 29, BlockType.sand, true))

    // 6. Outside Signpost
    // Pole at (22, 36) from foundationY to foundationY + 8
    for (let y = foundationY; y < foundationY + 9; y++) {
      this.customBlocks.push(new Block(22, y, 36, BlockType.obsidian, true))
    }
    // Signboard 5x5 vertical pattern for letter "M" (Sand blocks)
    const signPattern = [
      [BlockType.sand, BlockType.glass, BlockType.glass, BlockType.glass, BlockType.sand],
      [BlockType.sand, BlockType.sand, BlockType.glass, BlockType.sand, BlockType.sand],
      [BlockType.sand, BlockType.glass, BlockType.sand, BlockType.glass, BlockType.sand],
      [BlockType.sand, BlockType.glass, BlockType.glass, BlockType.glass, BlockType.sand],
      [BlockType.sand, BlockType.glass, BlockType.glass, BlockType.glass, BlockType.sand]
    ]
    for (let row = 0; row < 5; row++) {
      const yVal = foundationY + 13 - row
      const rowBlocks = signPattern[row]
      for (let col = 0; col < 5; col++) {
        const zVal = 34 + col
        const type = rowBlocks[col]
        if (type !== BlockType.glass) {
          this.customBlocks.push(new Block(22, yVal, zVal, type, true))
        }
      }
    }

    this.markCustomBlocksDirty()
  }

  update = () => {
    this.fluid.update()
    this.materials.tickAnimations(performance.now())

    this.chunk.set(
      Math.floor(this.camera.position.x / this.chunkSize),
      Math.floor(this.camera.position.z / this.chunkSize)
    )

    //generate terrain when getting into new chunk
    if (
      this.chunk.x !== this.previousChunk.x ||
      this.chunk.y !== this.previousChunk.y
    ) {
      this.generate()
    }

    this.previousChunk.copy(this.chunk)

    this.highlight.update()
  }
}
